import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { toast } from 'react-toastify'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Tooltip } from '@mui/material'
import CustomInput from '../../../components/muiComponents/customInput'
import { capitalize } from '../../../utils/functions'
import { ui, tone } from '../../../utils/uiTokens'
import {
    SequentialMode,
    StudentControlSubject,
    WITHDRAW_REASONS,
    studentControlApi,
} from '../../../features/curator/studentControl'

const MODES: SequentialMode[] = ['INHERIT', 'ON', 'OFF']

// yyyy-MM-dd -> dd.MM.yyyy
export const formatDay = (date?: string | null) => (date ? date.split('-').reverse().join('.') : '')

const today = () => {
    const d = new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

const chip = (t: { text: string; bg: string; border: string }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 10px',
    borderRadius: '24px',
    fontSize: '13px',
    fontWeight: 600,
    color: t.text,
    backgroundColor: t.bg,
    border: `1px solid ${t.border}`,
    whiteSpace: 'nowrap' as const,
})

interface Props {
    userCourseEnrollId: number
    // модератор: блок на каждый курс ученика
    title?: string
}

// Строгий режим ученика и «шықты» по предметам. Права решает бэкенд (canSetSequential / canManage).
const StudentControlBlock: React.FC<Props> = ({ userCourseEnrollId, title }) => {
    const { formatMessage, locale } = useIntl()
    const { data, isError } = studentControlApi.useFetchStudentControlQuery(userCourseEnrollId, { skip: !userCourseEnrollId })
    const [setMode, { isLoading: savingMode }] = studentControlApi.useSetSequentialModeMutation()
    const [restore, { isLoading: restoring }] = studentControlApi.useRestoreWithdrawnMutation()
    const [markWithdrawn, { isLoading: marking }] = studentControlApi.useMarkWithdrawnMutation()
    const [target, setTarget] = useState<StudentControlSubject | null>(null)
    const [form, setForm] = useState({ date: today(), reason: 'MOVED', note: '' })

    if (isError || !data) return null
    // ментору строгий режим курса - лишнее; без вышедших предметов блок пустой
    const showMode = data.canSetSequential || !!title
    if (!showMode && !data.subjects?.some((s) => s.withdrawnAt || s.returned || s.canManage)) return null

    const mode: SequentialMode = data.sequentialMode || 'INHERIT'
    const modeLabel = (m: SequentialMode) => formatMessage({ id: `strictMode${m}` })

    const handleMode = async (m: SequentialMode) => {
        if (m === mode) return
        try {
            await setMode({ userCourseEnrollId, mode: m }).unwrap()
            toast.success(formatMessage({ id: 'savedSuccess' }))
        } catch (e) {}
    }

    const handleRestore = async (s: StudentControlSubject) => {
        try {
            await restore({ userCourseEnrollId, userSubjectEnrollId: s.userSubjectEnrollId }).unwrap()
            toast.success(formatMessage({ id: 'savedSuccess' }))
        } catch (e) {}
    }

    const openDialog = (s: StudentControlSubject) => {
        setForm({ date: today(), reason: 'MOVED', note: '' })
        setTarget(s)
    }

    const handleWithdraw = async () => {
        if (!target || !form.date) return
        try {
            await markWithdrawn({
                userCourseEnrollId,
                userSubjectEnrollId: target.userSubjectEnrollId,
                date: form.date,
                reason: form.reason,
                note: form.note.trim() || undefined,
            }).unwrap()
            toast.success(formatMessage({ id: 'savedSuccess' }))
            setTarget(null)
        } catch (e) {}
    }

    const effective = formatMessage({ id: data.sequentialEffective ? 'strictModeON' : 'strictModeOFF' })

    return (
        <Box sx={styles.card}>
            {title && <Box sx={{ color: ui.ink, fontSize: '15px', fontWeight: 600 }}>{title}</Box>}
            {showMode && <Box sx={styles.row}>
                <Box sx={styles.label}>{formatMessage({ id: 'strictMode' })}</Box>
                {data.canSetSequential ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {MODES.map((m) => (
                            <Box
                                key={m}
                                component="button"
                                type="button"
                                disabled={savingMode}
                                onClick={() => handleMode(m)}
                                sx={styles.pill(m === mode)}
                            >
                                {modeLabel(m)}
                            </Box>
                        ))}
                    </Box>
                ) : (
                    <Box sx={{ color: ui.ink, fontWeight: 600, fontSize: '14px' }}>{modeLabel(mode)}</Box>
                )}
                {mode === 'INHERIT' && (
                    <Box sx={{ color: ui.muted, fontSize: '13px' }}>
                        {formatMessage({ id: 'strictModeNow' }, { state: effective.toLowerCase() })}
                    </Box>
                )}
            </Box>}

            {data.subjects?.map((s) => {
                const title = s[`subjectTitle${capitalize(locale)}`] || s.subjectTitleKz || s.subjectTitleRu
                const hint = [
                    s.withdrawReason && formatMessage({ id: `withdrawReason${s.withdrawReason}` }),
                    s.withdrawnByName && formatMessage({ id: 'withdrawnBy' }, { name: s.withdrawnByName }),
                ].filter(Boolean).join(' · ')

                return (
                    <Box key={s.userSubjectEnrollId} sx={styles.subjectRow}>
                        <Box sx={{ color: s.withdrawnAt ? ui.muted : ui.ink, fontSize: '14px', fontWeight: 600, minWidth: 0, overflowWrap: 'anywhere' }}>
                            {title}
                        </Box>
                        {s.withdrawnAt && (
                            <Tooltip title={hint} disableHoverListener={!hint}>
                                <Box component="span" sx={chip(tone.neutral)}>
                                    {formatMessage({ id: 'withdrawnBadge' }, { date: formatDay(s.withdrawnAt) })}
                                </Box>
                            </Tooltip>
                        )}
                        {s.returned && (
                            <Box component="span" sx={chip(tone.warn)}>{formatMessage({ id: 'withdrawnReturned' })}</Box>
                        )}
                        {s.canManage && (
                            <Box sx={{ marginLeft: 'auto' }}>
                                {s.withdrawnAt ? (
                                    <Button size="small" variant="outlined" disabled={restoring} onClick={() => handleRestore(s)}>
                                        {formatMessage({ id: 'withdrawnRestore' })}
                                    </Button>
                                ) : (
                                    <Button size="small" variant="outlined" onClick={() => openDialog(s)}>
                                        {formatMessage({ id: 'withdrawnMark' })}
                                    </Button>
                                )}
                            </Box>
                        )}
                    </Box>
                )
            })}

            <Dialog open={!!target} onClose={() => setTarget(null)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ color: ui.ink, fontWeight: 600 }}>
                    {formatMessage({ id: 'withdrawnTitle' })}
                    {target && (
                        <Box sx={{ color: ui.sub, fontSize: '14px', fontWeight: 400 }}>
                            {target[`subjectTitle${capitalize(locale)}`] || target.subjectTitleKz}
                        </Box>
                    )}
                </DialogTitle>
                <DialogContent>
                    <CustomInput
                        fullWidth
                        type="date"
                        label={formatMessage({ id: 'withdrawnDate' })}
                        InputLabelProps={{ shrink: true }}
                        value={form.date}
                        error={!form.date}
                        onChange={(e) => setForm({ ...form, date: e.target.value })}
                    />
                    <CustomInput
                        fullWidth
                        select
                        label={formatMessage({ id: 'reason' })}
                        value={form.reason}
                        onChange={(e) => setForm({ ...form, reason: e.target.value })}
                    >
                        {WITHDRAW_REASONS.map((r) => (
                            <MenuItem key={r} value={r}>{formatMessage({ id: `withdrawReason${r}` })}</MenuItem>
                        ))}
                    </CustomInput>
                    <CustomInput
                        fullWidth
                        multiline
                        minRows={2}
                        label={formatMessage({ id: 'withdrawnNote' })}
                        value={form.note}
                        onChange={(e) => setForm({ ...form, note: e.target.value })}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setTarget(null)}>{formatMessage({ id: 'cancel' })}</Button>
                    <Button variant="contained" disabled={marking || !form.date} onClick={handleWithdraw}>
                        {formatMessage({ id: 'save' })}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}

export default StudentControlBlock

const styles = {
    card: {
        gridColumn: '1 / -1',
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '12px',
        padding: '16px 20px',
        borderRadius: '16px',
        border: `1px solid ${ui.border}`,
        backgroundColor: ui.card,
        minWidth: 0,
    },
    row: {
        display: 'flex',
        flexWrap: 'wrap' as const,
        alignItems: 'center',
        gap: '12px',
    },
    label: {
        color: ui.sub,
        fontSize: '14px',
        fontWeight: 600,
    },
    subjectRow: {
        display: 'flex',
        flexWrap: 'wrap' as const,
        alignItems: 'center',
        gap: '8px',
        paddingTop: '12px',
        borderTop: `1px solid ${ui.border}`,
    },
    pill: (active: boolean) => ({
        cursor: 'pointer',
        font: 'inherit',
        fontSize: '13.5px',
        lineHeight: 1.2,
        padding: '7px 14px',
        borderRadius: '12px',
        whiteSpace: 'nowrap' as const,
        background: active ? ui.pinkSoft : 'transparent',
        border: `1px solid ${active ? 'transparent' : ui.border}`,
        color: active ? ui.pink : ui.sub,
        fontWeight: active ? 600 : 400,
        '&:hover': { borderColor: ui.pink, color: ui.pink },
        '&:disabled': { cursor: 'default', opacity: 0.6 },
    }),
}
